// Business-day arithmetic for follow-up date chips. Skips weekends (Sat/Sun)
// and Swedish public holidays (röda dagar), so a "call in N days" follow-up
// never lands on a day the BDR isn't working.
//
// Calendar-day keys use local Date getters, including around midnight and DST.

import { OUTCOMES, type OutcomeKind } from '@/shared/domain'

function isWeekend(d: Date): boolean {
  const dow = d.getDay()  // 0 = Sun, 6 = Sat
  return dow === 0 || dow === 6
}

const pad = (n: number) => String(n).padStart(2, '0')
/** Local calendar-day key, matching how holidays are generated. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Easter Sunday (Gregorian, Meeus/Jones/Butcher algorithm) as a local Date.
// Anchors every movable Swedish holiday.
function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

const _cache = new Map<number, Set<string>>()

/** Swedish public holidays (röda dagar) for a year, as local yyyy-mm-dd keys.
 * Includes the de-facto closed days (jul-/nyårs-/midsommarafton) since a BDR
 * won't be calling then either. Weekend-only holidays (Easter/Whit Sunday,
 * Midsummer/All Saints' day) are covered by the weekend check but listed for
 * completeness. */
export function swedishHolidays(year: number): Set<string> {
  const cached = _cache.get(year)
  if (cached) return cached

  const easter = easterSunday(year)
  const dates: Date[] = [
    new Date(year, 0, 1),    // Nyårsdagen
    new Date(year, 0, 6),    // Trettondedag jul
    addDays(easter, -2),     // Långfredag
    easter,                  // Påskdagen (Sun)
    addDays(easter, 1),      // Annandag påsk
    new Date(year, 4, 1),    // Första maj
    addDays(easter, 39),     // Kristi himmelsfärdsdag
    addDays(easter, 49),     // Pingstdagen (Sun)
    new Date(year, 5, 6),    // Nationaldagen
    new Date(year, 11, 24),  // Julafton (de facto)
    new Date(year, 11, 25),  // Juldagen
    new Date(year, 11, 26),  // Annandag jul
    new Date(year, 11, 31),  // Nyårsafton (de facto)
  ]

  // Midsummer Eve = the Friday in Jun 19–25 (Midsummer Day is the Sat after).
  for (let day = 19; day <= 25; day++) {
    const d = new Date(year, 5, day)
    if (d.getDay() === 5) { dates.push(d); dates.push(addDays(d, 1)); break }
  }

  const set = new Set(dates.map(dayKey))
  _cache.set(year, set)
  return set
}

function isHoliday(d: Date): boolean {
  return swedishHolidays(d.getFullYear()).has(dayKey(d))
}

export function isBusinessDay(d: Date): boolean {
  return !isWeekend(d) && !isHoliday(d)
}

// Advance the date forward until it lands on a business day. Returns the
// passed-in date unmodified when it's already a business day. Always returns
// a new Date instance — callers can mutate freely.
export function nextBusinessDay(d: Date): Date {
  const x = new Date(d)
  while (!isBusinessDay(x)) {
    x.setDate(x.getDate() + 1)
  }
  return x
}

/** Defaults and cleared-date fallbacks follow the same rules as date chips. */
export function defaultNextDate(kind: OutcomeKind, now = new Date()): string {
  const date = addDays(now, OUTCOMES[kind].defaultDays || 0)
  return dayKey(kind === 'booked' ? date : nextBusinessDay(date))
}
